import { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * PasswordInput — a drop-in replacement for `<Input type="password" />`
 * that adds an eye / eye-off toggle to reveal the value in plaintext.
 *
 * Usage notes:
 *  - Forwards refs and arbitrary props (autoComplete, minLength, required,
 *    name, id, className, ...) to the underlying Input so existing form
 *    logic keeps working unchanged.
 *  - Test ID convention: pass `data-testid` for the input itself; the
 *    eye toggle button derives `${testid}-toggle` automatically so tests
 *    can find it without extra wiring.
 *  - Accessible label flips between "show" and "hide" based on state.
 *
 * Phase M (Apr 2026) — added so every credential capture surface in the
 * app gives users the option to verify what they typed before submitting.
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { className, "data-testid": testId, ...props },
  ref,
) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const labelKey = shown ? "common.hidePassword" : "common.showPassword";
  const fallback = shown ? "Hide password" : "Show password";
  return (
    <div className="relative">
      <Input
        ref={ref}
        {...props}
        type={shown ? "text" : "password"}
        data-testid={testId}
        className={cn("pr-10", className)}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={t(labelKey, fallback)}
        title={t(labelKey, fallback)}
        onClick={() => setShown((v) => !v)}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground hover:text-foreground transition-colors"
      >
        {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
